import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import BankManagerDialog from './activities/BankManagerDialog';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ChevronLeft, ChevronRight, Plus, Edit2, Trash2, Printer, MapPin, Clock,
  ArrowLeftRight, X, Copy, GripVertical, Tag, BookOpen, Palette, Settings2, StickyNote, AlertTriangle
} from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday } from 'date-fns';
import { formatTime, formatTimeRange } from '../utils/time';
import logger from '../utils/logger';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const GROUPS = [1, 2, 3, 4, 5, 6, 7, 8];
const GROUP_COLORS = ['#7C3AED', '#9B7ED8', '#6366F1', '#8B5CF6', '#A78BFA', '#7C3AED', '#6D28D9', '#5B21B6'];

const CATEGORIES = {
  community: { label: 'Community', color: '#2E7D32', bg: 'bg-[#E8F5E9]', border: 'border-[#81C784]' },
  recreation: { label: 'Recreation', color: '#1565C0', bg: 'bg-[#E3F2FD]', border: 'border-[#64B5F6]' },
  educational: { label: 'Educational', color: '#E65100', bg: 'bg-[#FFF3E0]', border: 'border-[#FFB74D]' },
  life_skills: { label: 'Life Skills', color: '#6B3FA0', bg: 'bg-[#EDE7F6]', border: 'border-[#9B7ED8]' },
  volunteer: { label: 'Volunteer', color: '#AD1457', bg: 'bg-[#FCE4EC]', border: 'border-[#F06292]' },
  other: { label: 'Other', color: '#5A4D6E', bg: 'bg-[#F0EDF6]', border: 'border-[#DED8EB]' },
};

function CategoryBadge({ category }) {
  if (!category || !CATEGORIES[category]) return null;
  const c = CATEGORIES[category];
  return <Badge className={`text-[9px] ${c.bg} ${c.border} border`} style={{ color: c.color }}>{c.label}</Badge>;
}

function ActivityChip({ activity, index, onEdit, onDelete, onSwap, canEdit, dragHandleProps }) {
  const cat = CATEGORIES[activity.category];
  const hasTime = activity.time_start && activity.time_end;
  return (
    <div className={`flex items-center gap-1.5 p-2 rounded-md bg-white border text-xs group hover:border-[#9B7ED8] transition-colors ${cat ? cat.border : 'border-[#DED8EB]'}`}
      data-testid={`activity-${activity.id}`}>
      {canEdit && dragHandleProps && (
        <span {...dragHandleProps} className="cursor-grab text-[#DED8EB] hover:text-[#9B7ED8] shrink-0"><GripVertical className="h-3 w-3" /></span>
      )}
      <span className="font-mono text-[11px] font-semibold text-[#6B3FA0] shrink-0 w-5 text-center" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {(index ?? 0) + 1}.
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="font-medium text-[#2D1B4E] truncate">{activity.activity_name}</span>
          {activity.mandatory && (
            <Badge className="text-[8px] bg-red-100 border border-red-300 text-red-700 font-bold px-1 py-0" data-testid={`mandatory-badge-${activity.id}`}>
              MANDATORY
            </Badge>
          )}
          <CategoryBadge category={activity.category} />
          {hasTime && (
            <span className="text-[9px] text-[#8E85A0] flex items-center gap-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }} title="Scheduled time">
              <Clock className="h-2.5 w-2.5" />{formatTimeRange(activity.time_start, activity.time_end)}
            </span>
          )}
        </div>
        {activity.location && <p className="text-[10px] text-[#8E85A0] truncate flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{activity.location}</p>}
        {activity.notes && <p className="text-[10px] text-[#8E85A0] truncate italic">{activity.notes}</p>}
      </div>
      {canEdit && (
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={() => onSwap(activity)} className="text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`swap-${activity.id}`}><ArrowLeftRight className="h-3 w-3" /></button>
          <button onClick={() => onEdit(activity)} className="text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-act-${activity.id}`}><Edit2 className="h-3 w-3" /></button>
          <button onClick={() => onDelete(activity.id)} className="text-[#8E85A0] hover:text-[#D9534F]" data-testid={`del-act-${activity.id}`}><Trash2 className="h-3 w-3" /></button>
        </div>
      )}
    </div>
  );
}

function PrintView({ activities, shifts, date, weekDates, printMode, groupNotes = [] }) {
  const dateLabel = printMode === 'week'
    ? `Week of ${format(weekDates[0], 'MMMM d')} - ${format(weekDates[6], 'MMMM d, yyyy')}`
    : format(new Date(date + 'T12:00:00'), 'EEEE, MMMM d, yyyy');

  const datesToPrint = printMode === 'week' ? weekDates.map(d => format(d, 'yyyy-MM-dd')) : [date];

  return (
    <div className="print-only hidden print:block">
      <div className="text-center mb-4 border-b-2 border-[#6B3FA0] pb-3">
        <h1 className="text-2xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>The Next Step Programs</h1>
        <h2 className="text-lg font-semibold text-gray-700">Explore Program Activity Schedule</h2>
        <p className="text-sm text-gray-500">{dateLabel}</p>
      </div>
      {datesToPrint.map(dateStr => {
        const dayActivities = activities.filter(a => a.date === dateStr);
        if (dayActivities.length === 0 && printMode === 'week') return null;
        return (
          <div key={dateStr} className="mb-6 print:break-inside-avoid">
            {printMode === 'week' && (
              <h3 className="text-base font-bold text-black border-b border-gray-300 pb-1 mb-2">
                {format(new Date(dateStr + 'T12:00:00'), 'EEEE, MMMM d')}
              </h3>
            )}
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-1 text-left w-20">Group</th>
                  <th className="border border-gray-300 px-2 py-1 text-left w-28">Staff</th>
                  <th className="border border-gray-300 px-2 py-1 text-left w-24">Time</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Activity</th>
                  <th className="border border-gray-300 px-2 py-1 text-left w-20">Category</th>
                  <th className="border border-gray-300 px-2 py-1 text-left w-32">Location</th>
                  <th className="border border-gray-300 px-2 py-1 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {GROUPS.map(gNum => {
                  const gActs = dayActivities.filter(a => a.group_number === gNum).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.time_start.localeCompare(b.time_start));
                  const staff = shifts.find(s => s.group_number === gNum && s.date === dateStr)?.employee_name || '—';
                  const gNote = groupNotes.find(n => n.group_number === gNum && n.date === dateStr)?.note || '';
                  const noteRow = gNote ? (
                    <tr key={`note-${gNum}`}>
                      <td className="border border-gray-300 px-2 py-1 text-xs bg-amber-50 italic" colSpan="7">
                        <strong>Group {gNum} note:</strong> {gNote}
                      </td>
                    </tr>
                  ) : null;
                  if (gActs.length === 0) {
                    return (
                      <React.Fragment key={gNum}>
                        {noteRow}
                        <tr>
                          <td className="border border-gray-300 px-2 py-1 font-semibold">Group {gNum}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-500">{staff}</td>
                          <td className="border border-gray-300 px-2 py-1 text-gray-400" colSpan="5">No activities planned</td>
                        </tr>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={gNum}>
                      {noteRow}
                      {gActs.map((act, idx) => (
                        <tr key={act.id}>
                          {idx === 0 && <td className="border border-gray-300 px-2 py-1 font-semibold align-top" rowSpan={gActs.length}>Group {gNum}</td>}
                          {idx === 0 && <td className="border border-gray-300 px-2 py-1 text-gray-600 align-top" rowSpan={gActs.length}>{staff}</td>}
                          <td className="border border-gray-300 px-2 py-1 font-mono text-xs">{formatTimeRange(act.time_start, act.time_end)}</td>
                          <td className="border border-gray-300 px-2 py-1 font-medium">{act.mandatory ? '★ ' : ''}{act.activity_name}{act.mandatory ? ' (MANDATORY)' : ''}</td>
                          <td className="border border-gray-300 px-2 py-1 text-xs">{CATEGORIES[act.category]?.label || ''}</td>
                          <td className="border border-gray-300 px-2 py-1 text-xs text-gray-600">{act.location || ''}</td>
                          <td className="border border-gray-300 px-2 py-1 text-xs text-gray-500 italic">{act.notes || ''}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export default function ActivitiesPage() {
  const { api, user } = useAuth();
  const isScheduler = ['administrator', 'supervisor', 'scheduler'].includes(user?.role);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activities, setActivities] = useState([]);
  const [activityBank, setActivityBank] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [timePresets, setTimePresets] = useState([]);
  const [groupNotes, setGroupNotes] = useState([]);
  const [noteEdit, setNoteEdit] = useState(null);   // {group, text}
  const [noteSaving, setNoteSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ group_number: 1, date: '', time_start: '09:00', time_end: '10:30', activity_name: '', location: '', notes: '', category: '', mandatory: false });
  const [saving, setSaving] = useState(false);
  const [bankInput, setBankInput] = useState('');

  const [swapOpen, setSwapOpen] = useState(false);
  const [swapActivity, setSwapActivity] = useState(null);
  const [swapTarget, setSwapTarget] = useState('');

  // Copy dialog
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState({ group: '', date: '' });
  const [copyTarget, setCopyTarget] = useState({ group: '', date: '' });
  const [copyResult, setCopyResult] = useState(null);
  const [copying, setCopying] = useState(false);

  // Preset dialog
  const [presetOpen, setPresetOpen] = useState(false);
  const [editPresets, setEditPresets] = useState([]);

  // Print
  const [printMode, setPrintMode] = useState('day');

  // Drag-and-drop + bank search
  const [bankSearch, setBankSearch] = useState('');
  const [draggingName, setDraggingName] = useState('');       // bank-item drag in flight
  const [draggingActivityId, setDraggingActivityId] = useState(''); // reorder drag
  const [dropTargetGroup, setDropTargetGroup] = useState(0);  // group highlighted on hover
  const [pickerForBank, setPickerForBank] = useState(null);   // {item} → tap-to-add fallback
  const [bankManagerOpen, setBankManagerOpen] = useState(false);  // full bank manager dialog

  // Bulk-edit: one dialog to edit every activity in a group/day at once
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState(null);
  const [bulkDate, setBulkDate] = useState('');
  const [bulkRows, setBulkRows] = useState([]);   // [{id?, activity_name, category, location, notes, time_start, time_end}]
  const [bulkSaving, setBulkSaving] = useState(false);

  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);

  const activityCountsByDate = useMemo(() => {
    const counts = {};
    activities.forEach(a => { counts[a.date] = (counts[a.date] || 0) + 1; });
    return counts;
  }, [activities]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [actRes, bankRes, shiftRes, presetRes, notesRes] = await Promise.all([
        api.get(`/activities?week_start=${weekStart}`),
        api.get('/activity-bank'),
        api.get(`/shifts?week_start=${weekStart}&service_line=day_program`),
        api.get('/activity-presets'),
        api.get(`/group-notes?week_start=${weekStart}`).catch(() => ({ data: [] })),
      ]);
      setActivities(actRes.data);
      setActivityBank(bankRes.data);
      setShifts(shiftRes.data);
      setTimePresets(presetRes.data?.blocks || []);
      setGroupNotes(notesRes.data || []);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getGroupActivities = (groupNum, date) => {
    return activities.filter(a => a.group_number === groupNum && a.date === date)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.time_start || '').localeCompare(b.time_start || ''));
  };

  const getGroupStaff = (groupNum, date) => {
    // Split-day support: a group can have multiple non-overlapping shifts on
    // the same day (AM DSP + PM DSP). Return the joined list, or null if none.
    const shiftsForGroup = shifts
      .filter(s => s.group_number === groupNum && s.date === date && s.employee_name)
      .sort((a, b) => (a.time_in || '').localeCompare(b.time_in || ''));
    if (shiftsForGroup.length === 0) return null;
    if (shiftsForGroup.length === 1) return shiftsForGroup[0].employee_name;
    return shiftsForGroup.map(s => `${s.employee_name} (${s.time_in || '—'})`).join(' → ');
  };

  // Participant first names assigned to this group on this date — used to
  // show "Staff · Participant A, Participant B" in the group card header so
  // schedulers don't have to cross-reference another view. Merges names
  // across split shifts (deduplicated) so participants show once even if
  // both AM and PM shifts have them listed.
  const getGroupParticipants = (groupNum, date) => {
    const shiftsForGroup = shifts.filter(s => s.group_number === groupNum && s.date === date);
    const all = shiftsForGroup.flatMap(s => s.participants || []);
    const uniq = Array.from(new Set(all.map(n => (n || '').trim()).filter(Boolean)));
    return uniq.map(n => n.split(/\s+/)[0]).filter(Boolean);
  };

  // Tracks the extra group numbers checked in the "Also apply to" row
  const [extraGroups, setExtraGroups] = useState([]);

  const openAdd = (groupNum, date) => {
    setEditing(null);
    setExtraGroups([]);
    setForm({ group_number: groupNum, date, time_start: '', time_end: '', activity_name: '', location: '', notes: '', category: '', mandatory: false });
    setDialogOpen(true);
  };

  const openEdit = (act) => {
    setEditing(act);
    setExtraGroups([]);
    setForm({ group_number: act.group_number, date: act.date, time_start: act.time_start, time_end: act.time_end, activity_name: act.activity_name, location: act.location || '', notes: act.notes || '', category: act.category || '', mandatory: !!act.mandatory });
    setDialogOpen(true);
  };

  // Bulk edit — one dialog listing every activity for a group/day. Users can
  // rename, re-category, re-order, add or delete without touching each chip.
  const openBulkEdit = (groupNum, date) => {
    const rows = getGroupActivities(groupNum, date).map(a => ({
      id: a.id,
      activity_name: a.activity_name,
      category: a.category || '',
      location: a.location || '',
      notes: a.notes || '',
      time_start: a.time_start || '',
      time_end: a.time_end || '',
      mandatory: !!a.mandatory,
    }));
    setBulkGroup(groupNum);
    setBulkDate(date);
    setBulkRows(rows.length ? rows : [{ id: null, activity_name: '', category: '', location: '', notes: '', time_start: '', time_end: '', mandatory: false }]);
    setBulkOpen(true);
  };

  const updateBulkRow = (idx, patch) => {
    setBulkRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeBulkRow = (idx) => setBulkRows(prev => prev.filter((_, i) => i !== idx));
  const addBulkRow = () => setBulkRows(prev => [...prev, { id: null, activity_name: '', category: '', location: '', notes: '', time_start: '', time_end: '', mandatory: false }]);
  const moveBulkRow = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= bulkRows.length) return;
    setBulkRows(prev => { const c = [...prev]; [c[idx], c[j]] = [c[j], c[idx]]; return c; });
  };

  const saveBulk = async () => {
    setBulkSaving(true);
    try {
      const rows = bulkRows.filter(r => (r.activity_name || '').trim());
      await api.post('/activities/bulk-save', {
        group_number: bulkGroup,
        date: bulkDate,
        rows,
      });
      toast.success(`Group ${bulkGroup} activities saved`);
      setBulkOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk save failed');
    } finally { setBulkSaving(false); }
  };

  const getGroupNote = (g, date) => groupNotes.find(n => n.group_number === g && n.date === date)?.note || '';

  const saveGroupNote = async () => {
    if (!noteEdit) return;
    setNoteSaving(true);
    try {
      await api.put('/group-notes', { group_number: noteEdit.group, date: selectedDate, note: noteEdit.text });
      toast.success(noteEdit.text.trim() ? `Group ${noteEdit.group} note saved` : `Group ${noteEdit.group} note cleared`);
      setNoteEdit(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Note save failed'); }
    setNoteSaving(false);
  };

  // Build an Add-Activity form payload from a rich bank entry. Auto-fills:
  //   name      → bank item name
  //   location  → bank item address
  //   notes     → bullet-list of address / phone / email / website / freeform notes
  //   time_end  → time_start + default_duration_min (if set)
  const formFromBankItem = useCallback((item, groupNum, date) => {
    const start = timePresets[0]?.time_start || '09:00';
    let end = timePresets[0]?.time_end || '10:30';
    if (item.default_duration_min) {
      const [h, m] = start.split(':').map(Number);
      const total = h * 60 + m + item.default_duration_min;
      end = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    }
    const noteParts = [];
    if (item.address) noteParts.push(`📍 ${item.address}`);
    if (item.phone) noteParts.push(`☎ ${item.phone}`);
    if (item.email) noteParts.push(`✉ ${item.email}`);
    if (item.website) noteParts.push(`🌐 ${item.website}`);
    if (item.notes) noteParts.push(item.notes);
    return {
      group_number: groupNum,
      date,
      time_start: start,
      time_end: end,
      activity_name: item.name,
      location: item.address || '',
      notes: noteParts.join('\n'),
      category: '',
      mandatory: false,
    };
  }, [timePresets]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/activities/${editing.id}`, { time_start: form.time_start, time_end: form.time_end, activity_name: form.activity_name, location: form.location, notes: form.notes, category: form.category, mandatory: !!form.mandatory });
      } else {
        // Create for primary group + each checked "also apply to" group, in parallel
        const targets = [form.group_number, ...extraGroups.filter(g => g !== form.group_number)];
        await Promise.all(targets.map(g => api.post('/activities', { ...form, group_number: g })));
        if (targets.length > 1) toast.success(`Added to ${targets.length} groups`);
      }
      setDialogOpen(false);
      setExtraGroups([]);
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    await api.delete(`/activities/${id}`);
    fetchData();
  };

  const openSwap = (act) => { setSwapActivity(act); setSwapTarget(''); setSwapOpen(true); };
  const handleSwap = async () => {
    if (!swapActivity || !swapTarget) return;
    try {
      await api.post(`/activities/swap?activity_id=${swapActivity.id}&new_activity_name=${encodeURIComponent(swapTarget)}`);
      setSwapOpen(false); fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Not in approved bank'); }
  };

  const addToBank = async () => {
    if (!bankInput.trim()) return;
    try { await api.post(`/activity-bank?name=${encodeURIComponent(bankInput.trim())}`); setBankInput(''); fetchData(); } catch (e) { logger.error(e); }
  };

  const deleteFromBank = async (id) => {
    await api.delete(`/activity-bank/${id}`);
    fetchData();
  };

  // Copy activities
  const handleCopy = async () => {
    setCopying(true);
    setCopyResult(null);
    try {
      const { data } = await api.post('/activities/copy', {
        source_group: copySource.group ? parseInt(copySource.group) : null,
        source_date: copySource.date || selectedDate,
        target_group: copyTarget.group ? parseInt(copyTarget.group) : null,
        target_date: copyTarget.date || selectedDate
      });
      setCopyResult(data);
      fetchData();
    } catch (e) { setCopyResult({ error: e.response?.data?.detail || 'Copy failed' }); }
    setCopying(false);
  };

  // Move activity up/down within group
  const moveActivity = async (groupNum, date, actIndex, direction) => {
    const acts = getGroupActivities(groupNum, date);
    const newIndex = actIndex + direction;
    if (newIndex < 0 || newIndex >= acts.length) return;
    const reordered = [...acts];
    [reordered[actIndex], reordered[newIndex]] = [reordered[newIndex], reordered[actIndex]];
    try {
      await api.post('/activities/reorder', { activity_ids: reordered.map(a => a.id) });
      fetchData();
    } catch (e) { logger.error(e); }
  };

  // Save time presets
  const handleSavePresets = async () => {
    try {
      await api.put('/activity-presets', { blocks: editPresets });
      setPresetOpen(false);
      fetchData();
    } catch (e) { alert('Error saving presets'); }
  };

  const handlePrint = () => window.print();

  const applyPreset = (preset) => {
    setForm(p => ({ ...p, time_start: preset.time_start, time_end: preset.time_end }));
  };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="activities-page">
      {/* Print view (hidden on screen, visible when printing) */}
      <PrintView activities={activities} shifts={shifts} date={selectedDate} weekDates={weekDates} printMode={printMode} groupNotes={groupNotes} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Activities</h1>
          <p className="text-base text-[#5A4D6E] mt-1">Explore Program group activities — Week of {format(currentWeek, 'MMM d, yyyy')}</p>
        </div>
        <div className="flex items-center gap-2">
          {isScheduler && (
            <>
              <Button variant="outline" onClick={() => { setCopySource({ group: '', date: selectedDate }); setCopyTarget({ group: '', date: '' }); setCopyResult(null); setCopyOpen(true); }}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="copy-activities-btn">
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
              <Button variant="outline" onClick={() => { setEditPresets([...timePresets]); setPresetOpen(true); }}
                className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1.5" data-testid="presets-btn">
                <Settings2 className="h-3.5 w-3.5" /> Time Blocks
              </Button>
            </>
          )}
          <div className="flex items-center border border-[#DED8EB] rounded-lg overflow-hidden">
            <button onClick={() => setPrintMode('day')}
              className={`px-2.5 py-1.5 text-xs font-medium ${printMode === 'day' ? 'bg-[#6B3FA0] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
              data-testid="print-mode-day">Day</button>
            <button onClick={() => setPrintMode('week')}
              className={`px-2.5 py-1.5 text-xs font-medium ${printMode === 'week' ? 'bg-[#6B3FA0] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
              data-testid="print-mode-week">Week</button>
          </div>
          <Button variant="outline" onClick={handlePrint} className="border-[#DED8EB] text-[#2D1B4E] gap-2" data-testid="print-btn">
            <Printer className="h-4 w-4" /> Print {printMode === 'week' ? 'Week' : 'Day'}
          </Button>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} className="border-[#DED8EB]"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => { setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 0 })); setSelectedDate(format(new Date(), 'yyyy-MM-dd')); }} className="border-[#DED8EB] text-[#2D1B4E]">Today</Button>
            <Button variant="outline" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} className="border-[#DED8EB]"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Day selector */}
      <div className="flex gap-2 print:hidden" data-testid="activity-day-selector">
        {weekDates.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const isSelected = dateStr === selectedDate;
          const today = isToday(date);
          const dayCount = activityCountsByDate[dateStr] || 0;
          return (
            <button key={dateStr} onClick={() => setSelectedDate(dateStr)}
              className={`flex-1 p-2 rounded-lg border text-center transition-all ${isSelected ? 'border-[#6B3FA0] bg-[#6B3FA0] text-white shadow-sm' : today ? 'border-[#6B3FA0] bg-white text-[#2D1B4E]' : 'border-[#DED8EB] bg-white text-[#5A4D6E] hover:border-[#9B7ED8]'}`}
              data-testid={`act-day-${dateStr}`}>
              <p className="text-xs font-semibold uppercase">{DAYS[date.getDay()]}</p>
              <p className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(date, 'd')}</p>
              {dayCount > 0 && <p className={`text-[10px] ${isSelected ? 'text-white/70' : 'text-[#8E85A0]'}`}>{dayCount} acts</p>}
            </button>
          );
        })}
      </div>

      {/* Activity bank — collapsed summary card. Click "Open Bank" for the
          full database manager (search, edit, photos, contact info). */}
      <Card className="border-[#DED8EB] shadow-sm bg-white print:hidden" data-testid="activity-bank">
        <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#EDE7F6] flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-[#6B3FA0]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#2D1B4E] leading-tight">Activity Bank</h3>
              <p className="text-[11px] text-[#8E85A0] leading-tight">
                {activityBank.length} {activityBank.length === 1 ? 'place' : 'places'} ·
                {isScheduler ? ' tap an entry inside to drop it on a group, or open the full database to edit details' : ' tap an entry to assign it'}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setBankManagerOpen(true)}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8 text-xs"
            data-testid="open-bank-btn">
            Open Bank →
          </Button>
        </CardContent>
      </Card>

      {/* Time block presets strip */}
      {timePresets.length > 0 && (
        <div className="flex items-center gap-2 print:hidden">
          <span className="text-xs font-semibold text-[#8E85A0] uppercase tracking-wider shrink-0">Time Blocks:</span>
          {timePresets.map((p, i) => (
            <Badge key={`preset-${p.name}-${i}`} className="text-[10px] bg-[#F0EDF6] text-[#5A4D6E] border border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {p.name}: {formatTimeRange(p.time_start, p.time_end)}
            </Badge>
          ))}
        </div>
      )}

      {/* Groups grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 print:hidden" data-testid="groups-activities-grid">
        {GROUPS.map(groupNum => {
          const acts = getGroupActivities(groupNum, selectedDate);
          const staffName = getGroupStaff(groupNum, selectedDate);
          const participantList = getGroupParticipants(groupNum, selectedDate);
          const isDropTarget = dropTargetGroup === groupNum;
          return (
            <Card
              key={groupNum}
              onDragOver={(e) => {
                if (!isScheduler || !draggingName) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                if (dropTargetGroup !== groupNum) setDropTargetGroup(groupNum);
              }}
              onDragLeave={() => { if (dropTargetGroup === groupNum) setDropTargetGroup(0); }}
              onDrop={(e) => {
                if (!isScheduler) return;
                e.preventDefault();
                const name = e.dataTransfer.getData('text/plain') || draggingName;
                setDropTargetGroup(0);
                setDraggingName('');
                if (!name) return;
                // Open add dialog pre-filled — rich auto-fill from the bank
                setEditing(null);
                setExtraGroups([]);
                const item = activityBank.find(b => b.name === name);
                setForm(item ? formFromBankItem(item, groupNum, selectedDate) : {
                  group_number: groupNum, date: selectedDate,
                  time_start: timePresets[0]?.time_start || '09:00',
                  time_end: timePresets[0]?.time_end || '10:30',
                  activity_name: name, location: '', notes: '', category: '', mandatory: false,
                });
                setDialogOpen(true);
              }}
              className={`border shadow-sm bg-white transition-all ${isDropTarget ? 'border-[#6B3FA0] ring-2 ring-[#6B3FA0]/40 scale-[1.02]' : 'border-[#DED8EB]'}`}
              data-testid={`group-activities-${groupNum}`}>
              <CardHeader className="p-3 pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Badge className="text-[10px] text-white" style={{ backgroundColor: GROUP_COLORS[groupNum - 1] }}>Group {groupNum}</Badge>
                    {staffName && <p className="text-xs font-medium text-[#2D1B4E] mt-1 truncate" title={staffName}>{staffName}</p>}
                    {participantList.length > 0 && (
                      <p className="text-[10px] text-[#8E85A0] mt-0.5 leading-tight" data-testid={`group-participants-${groupNum}`}
                         title={`Participants: ${participantList.join(', ')}`}>
                        {participantList.length <= 3
                          ? participantList.join(', ')
                          : `${participantList.slice(0, 3).join(', ')} +${participantList.length - 3}`}
                      </p>
                    )}
                  </div>
                  {isScheduler && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      {acts.length > 0 && (
                        <Button variant="ghost" size="icon" onClick={() => openBulkEdit(groupNum, selectedDate)}
                                className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]"
                                title="Edit all activities in this group"
                                data-testid={`bulk-edit-g${groupNum}`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => openAdd(groupNum, selectedDate)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`add-activity-g${groupNum}`}>
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-1.5">
                {/* General group note — monitorings, dietary, day-specific instructions */}
                {noteEdit?.group === groupNum ? (
                  <div className="space-y-1" data-testid={`group-note-edit-${groupNum}`}>
                    <Textarea value={noteEdit.text}
                      onChange={e => setNoteEdit(n => ({ ...n, text: e.target.value }))}
                      placeholder="Monitoring visit today, dietary needs, participant instructions…"
                      className="border-amber-300 bg-amber-50 min-h-[60px] text-xs"
                      data-testid={`group-note-input-${groupNum}`} />
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => setNoteEdit(null)} data-testid={`group-note-cancel-${groupNum}`}>Cancel</Button>
                      <Button size="sm" className="h-6 text-[10px] px-2 bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" disabled={noteSaving}
                        onClick={saveGroupNote} data-testid={`group-note-save-${groupNum}`}>
                        {noteSaving ? 'Saving…' : 'Save note'}
                      </Button>
                    </div>
                  </div>
                ) : (() => {
                  const note = getGroupNote(groupNum, selectedDate);
                  if (note) return (
                    <div className="p-2 rounded-md bg-amber-50 border border-amber-200 relative" data-testid={`group-note-${groupNum}`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        <StickyNote className="h-2.5 w-2.5 text-amber-700" />
                        <span className="font-bold text-[9px] uppercase tracking-wider text-amber-700">Group note</span>
                        {isScheduler && (
                          <button onClick={() => setNoteEdit({ group: groupNum, text: note })}
                            className="ml-auto text-amber-500 hover:text-amber-800" title="Edit group note"
                            data-testid={`group-note-editbtn-${groupNum}`}>
                            <Edit2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-[11px] text-amber-900 whitespace-pre-wrap leading-snug">{note}</p>
                    </div>
                  );
                  if (isScheduler) return (
                    <button onClick={() => setNoteEdit({ group: groupNum, text: '' })}
                      className="w-full text-left text-[10px] text-[#5A4D6E] hover:text-[#6B3FA0] italic flex items-center gap-1"
                      data-testid={`group-note-add-${groupNum}`}>
                      <StickyNote className="h-2.5 w-2.5" /> + Group note (monitoring, dietary, instructions)
                    </button>
                  );
                  return null;
                })()}
                {acts.length === 0 ? (
                  <p className={`text-[10px] text-center py-3 ${isDropTarget ? 'text-[#6B3FA0] font-bold' : 'text-[#8E85A0]'}`}>
                    {isDropTarget ? 'Drop here to add' : (isScheduler ? 'No activities — drag from bank ↑' : 'No activities')}
                  </p>
                ) : (
                  acts.map((act, idx) => (
                    <div
                      key={act.id}
                      draggable={isScheduler}
                      onDragStart={(e) => {
                        if (!isScheduler) return;
                        setDraggingActivityId(act.id);
                        e.dataTransfer.setData('text/plain', '');
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => setDraggingActivityId('')}
                      onDragOver={(e) => {
                        if (!isScheduler || !draggingActivityId || draggingActivityId === act.id) return;
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        if (!isScheduler || !draggingActivityId || draggingActivityId === act.id) return;
                        e.preventDefault();
                        e.stopPropagation();
                        // Reorder: move dragged activity to this index within same group/date
                        const fromIdx = acts.findIndex(x => x.id === draggingActivityId);
                        if (fromIdx === -1 || fromIdx === idx) { setDraggingActivityId(''); return; }
                        const reordered = [...acts];
                        const [moved] = reordered.splice(fromIdx, 1);
                        reordered.splice(idx, 0, moved);
                        api.post('/activities/reorder', { activity_ids: reordered.map(a => a.id) })
                          .then(() => fetchData())
                          .catch(err => logger.error(err));
                        setDraggingActivityId('');
                      }}
                      className={`transition-all ${draggingActivityId === act.id ? 'opacity-50' : ''}`}>
                      <ActivityChip activity={act} index={idx} onEdit={openEdit} onDelete={handleDelete} onSwap={openSwap}
                        canEdit={isScheduler}
                        dragHandleProps={isScheduler ? { className: 'cursor-grab active:cursor-grabbing' } : null} />
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add/Edit Activity Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white max-h-[85vh] flex flex-col" data-testid="activity-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editing ? 'Edit Activity' : 'Add Activity'} — Group {form.group_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1">
            {/* Time block presets */}
            {timePresets.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-[#2D1B4E] font-medium text-sm">Quick Time Blocks</Label>
                <div className="flex gap-1.5">
                  {timePresets.map((p, i) => (
                    <button key={`dlg-preset-${p.name}-${i}`} type="button" onClick={() => applyPreset(p)}
                      className={`text-[10px] px-2 py-1 rounded-md border transition-all ${
                        form.time_start === p.time_start && form.time_end === p.time_end
                          ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]'
                          : 'bg-[#F0EDF6] text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8]'
                      }`} data-testid={`preset-${i}`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium text-sm">Start Time <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                <Input type="time" value={form.time_start} onChange={e => setForm(p => ({ ...p, time_start: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="act-time-start" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium text-sm">End Time <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
                <Input type="time" value={form.time_end} onChange={e => setForm(p => ({ ...p, time_end: e.target.value }))} className="border-[#DED8EB]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="act-time-end" />
              </div>
            </div>
            <p className="text-[10px] text-[#8E85A0] -mt-2">Skip the times for most activities. Add a time only when a specific slot matters (volunteering, appointments) — otherwise DSPs work through the list 1→5 in order.</p>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium text-sm">Activity</Label>
              <Input value={form.activity_name} onChange={e => setForm(p => ({ ...p, activity_name: e.target.value }))} placeholder="Type or pick from bank below" className="border-[#DED8EB]" data-testid="act-name" />
              {activityBank.length > 0 && (
                <div className="border border-[#DED8EB] rounded-md p-2 bg-[#FBFAFE]" data-testid="dialog-bank">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold text-[#8E85A0] uppercase tracking-wider">From bank ({activityBank.length})</span>
                    {activityBank.length > 8 && (
                      <Input value={bankSearch} onChange={e => setBankSearch(e.target.value)} placeholder="Filter…" className="border-[#DED8EB] h-6 w-32 text-[11px]" data-testid="dialog-bank-search" />
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {activityBank
                      .filter(a => !bankSearch.trim() || a.name.toLowerCase().includes(bankSearch.toLowerCase()))
                      .map(a => (
                        <button key={a.id} type="button" onClick={() => setForm(p => ({ ...p, activity_name: a.name }))}
                          className={`text-[10px] px-2 py-0.5 rounded-md border transition-all ${form.activity_name === a.name ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8] hover:bg-[#F0EDF6]'}`}
                          data-testid={`dialog-bank-${a.id}`}>
                          {a.name}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium text-sm flex items-center gap-1"><Tag className="h-3 w-3" /> Category</Label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(CATEGORIES).map(([key, c]) => (
                  <button key={key} type="button" onClick={() => setForm(p => ({ ...p, category: p.category === key ? '' : key }))}
                    className={`text-[10px] font-medium px-2 py-1 rounded-md border transition-all ${
                      form.category === key ? `${c.bg} ${c.border}` : 'bg-white text-[#8E85A0] border-[#DED8EB] hover:border-[#9B7ED8]'
                    }`} style={form.category === key ? { color: c.color } : {}} data-testid={`cat-${key}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={`flex items-start gap-2 p-2.5 rounded-md border transition-colors ${form.mandatory ? 'bg-red-50 border-red-300' : 'border-[#DED8EB]'}`}>
              <Checkbox id="act-mandatory" checked={!!form.mandatory}
                onCheckedChange={v => setForm(p => ({ ...p, mandatory: !!v }))}
                className="mt-0.5" data-testid="act-mandatory" />
              <div className="space-y-0.5">
                <Label htmlFor="act-mandatory" className="text-sm font-medium text-[#2D1B4E] cursor-pointer flex items-center gap-1">
                  <AlertTriangle className={`h-3 w-3 ${form.mandatory ? 'text-red-600' : 'text-[#8E85A0]'}`} /> Mandatory — can&apos;t miss
                </Label>
                <p className="text-[10px] text-[#8E85A0] leading-tight">Supervisors get an alert if this activity is changed, removed, or the group&apos;s staffing for the day is adjusted.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium text-sm">Location <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Address or description" className="border-[#DED8EB]" data-testid="act-location" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium text-sm">Notes & Instructions <span className="text-[#8E85A0] font-normal">(optional)</span></Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Bring swim gear, special instructions, dietary notes..." className="border-[#DED8EB] min-h-[70px]" data-testid="act-notes" />
            </div>
            {!editing && (
              <div className="space-y-2 pt-2 border-t border-[#DED8EB]" data-testid="copy-to-groups">
                <Label className="text-[#2D1B4E] font-medium text-sm">Also apply to groups <span className="text-[#8E85A0] font-normal">(optional — for off-site trips)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {GROUPS.filter(g => g !== form.group_number).map(g => {
                    const checked = extraGroups.includes(g);
                    const staffName = getGroupStaff(g, form.date);
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setExtraGroups(prev => checked ? prev.filter(x => x !== g) : [...prev, g])}
                        className={`text-[11px] px-2 py-1 rounded-md border transition-all flex items-center gap-1 ${checked ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8]'}`}
                        data-testid={`copy-to-g${g}`}
                        title={staffName || `Group ${g}`}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: GROUP_COLORS[g - 1] }} />
                        Group {g}{staffName && <span className="opacity-70 ml-0.5">({staffName.split(' ')[0]})</span>}
                      </button>
                    );
                  })}
                  {extraGroups.length > 0 && (
                    <button type="button" onClick={() => setExtraGroups([])} className="text-[11px] px-2 py-1 text-[#8E85A0] hover:text-[#D9534F]" data-testid="clear-copy-to">Clear</button>
                  )}
                </div>
                {extraGroups.length > 0 && (
                  <p className="text-[11px] text-[#6B3FA0] font-medium">Will create {extraGroups.length + 1} activities in one click.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.activity_name} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="act-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : (extraGroups.length > 0 ? `Add to ${extraGroups.length + 1} groups` : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Group Dialog — edit every activity in a group/day at once */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[90vh] flex flex-col" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Group {bulkGroup} — {bulkDate && format(new Date(bulkDate + 'T12:00:00'), 'EEE, MMM d')}
            </DialogTitle>
            <p className="text-xs text-[#8E85A0]">
              Rename, re-category, add, remove, or reorder every activity in one place. Times are optional — leave blank for list-style flow.
            </p>
          </DialogHeader>
          <div className="space-y-2 py-2 overflow-y-auto flex-1 pr-1">
            {bulkRows.map((row, idx) => {
              const cat = CATEGORIES[row.category];
              return (
                <div key={row.id || `new-${idx}`}
                     className={`p-2.5 rounded-lg border ${cat ? cat.border : 'border-[#DED8EB]'} bg-white space-y-2`}
                     data-testid={`bulk-row-${idx}`}>
                  <div className="flex items-center gap-1.5">
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button type="button" onClick={() => moveBulkRow(idx, -1)} disabled={idx === 0}
                              className="text-[#8E85A0] hover:text-[#6B3FA0] disabled:opacity-30 leading-none"
                              data-testid={`bulk-up-${idx}`} title="Move up">▲</button>
                      <button type="button" onClick={() => moveBulkRow(idx, 1)} disabled={idx === bulkRows.length - 1}
                              className="text-[#8E85A0] hover:text-[#6B3FA0] disabled:opacity-30 leading-none"
                              data-testid={`bulk-down-${idx}`} title="Move down">▼</button>
                    </div>
                    <span className="font-mono text-sm font-semibold text-[#6B3FA0] w-6 text-center">{idx + 1}.</span>
                    <Input value={row.activity_name}
                           onChange={e => updateBulkRow(idx, { activity_name: e.target.value })}
                           placeholder="Activity name"
                           className="border-[#DED8EB] flex-1 font-medium"
                           data-testid={`bulk-name-${idx}`} />
                    <button type="button" onClick={() => removeBulkRow(idx)}
                            className="text-[#8E85A0] hover:text-[#D9534F] shrink-0 p-1"
                            data-testid={`bulk-del-${idx}`} title="Delete row">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 pl-9">
                    <Input value={row.location}
                           onChange={e => updateBulkRow(idx, { location: e.target.value })}
                           placeholder="Location (optional)"
                           className="border-[#DED8EB] h-8 text-xs"
                           data-testid={`bulk-loc-${idx}`} />
                    <Input value={row.notes}
                           onChange={e => updateBulkRow(idx, { notes: e.target.value })}
                           placeholder="Notes — put times here too if needed"
                           className="border-[#DED8EB] h-8 text-xs sm:col-span-2"
                           data-testid={`bulk-notes-${idx}`} />
                  </div>
                  <div className="flex items-center gap-1 flex-wrap pl-9">
                    {Object.entries(CATEGORIES).map(([key, c]) => (
                      <button key={key} type="button"
                              onClick={() => updateBulkRow(idx, { category: row.category === key ? '' : key })}
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded border transition-all ${row.category === key ? `${c.bg} ${c.border}` : 'bg-white text-[#8E85A0] border-[#DED8EB] hover:border-[#9B7ED8]'}`}
                              style={row.category === key ? { color: c.color } : {}}
                              data-testid={`bulk-cat-${idx}-${key}`}>
                        {c.label}
                      </button>
                    ))}
                    <button type="button"
                            onClick={() => updateBulkRow(idx, { mandatory: !row.mandatory })}
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded border transition-all ${row.mandatory ? 'bg-red-100 border-red-300 text-red-700' : 'bg-white text-[#8E85A0] border-[#DED8EB] hover:border-red-300'}`}
                            title="Mandatory — supervisors alerted on changes"
                            data-testid={`bulk-mandatory-${idx}`}>
                      {row.mandatory ? '✓ ' : ''}MANDATORY
                    </button>
                  </div>
                </div>
              );
            })}
            <button type="button" onClick={addBulkRow}
                    className="w-full py-2 rounded-lg border border-dashed border-[#DED8EB] text-[#6B3FA0] hover:bg-[#F0EDF6] text-sm flex items-center justify-center gap-1"
                    data-testid="bulk-add-row">
              <Plus className="h-4 w-4" /> Add another activity
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="border-[#DED8EB]" data-testid="bulk-cancel">Cancel</Button>
            <Button onClick={saveBulk} disabled={bulkSaving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="bulk-save">
              {bulkSaving ? 'Saving…' : 'Save all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap Dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D1B4E]">Swap Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#5A4D6E]">Current: <strong>{swapActivity?.activity_name}</strong></p>
            <Label className="text-[#2D1B4E] font-medium text-sm">Replace with:</Label>
            <Select value={swapTarget} onValueChange={setSwapTarget}>
              <SelectTrigger className="border-[#DED8EB]" data-testid="swap-select"><SelectValue placeholder="Pick activity" /></SelectTrigger>
              <SelectContent>
                {activityBank.filter(a => a.name !== swapActivity?.activity_name).map(a => (
                  <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSwap} disabled={!swapTarget} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="swap-confirm-btn">Swap</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Activities Dialog */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="copy-activities-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Copy className="h-5 w-5 text-[#6B3FA0]" /> Copy Activities
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-[#F0EDF6] border border-[#DED8EB]">
              <p className="text-xs font-semibold text-[#8E85A0] uppercase tracking-wider mb-2">Copy From</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#5A4D6E]">Date</Label>
                  <Input type="date" value={copySource.date} onChange={e => setCopySource(p => ({ ...p, date: e.target.value }))} className="border-[#DED8EB] h-8 text-xs" data-testid="copy-src-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#5A4D6E]">Group <span className="text-[#8E85A0]">(blank=all)</span></Label>
                  <Select value={copySource.group} onValueChange={v => setCopySource(p => ({ ...p, group: v }))}>
                    <SelectTrigger className="border-[#DED8EB] h-8 text-xs" data-testid="copy-src-group"><SelectValue placeholder="All groups" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Groups</SelectItem>
                      {GROUPS.map(g => <SelectItem key={g} value={String(g)}>Group {g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-[#E8F5E9]/30 border border-[#81C784]">
              <p className="text-xs font-semibold text-[#2E7D32] uppercase tracking-wider mb-2">Copy To</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#5A4D6E]">Date</Label>
                  <Input type="date" value={copyTarget.date} onChange={e => setCopyTarget(p => ({ ...p, date: e.target.value }))} className="border-[#DED8EB] h-8 text-xs" data-testid="copy-tgt-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#5A4D6E]">Group <span className="text-[#8E85A0]">(blank=same)</span></Label>
                  <Select value={copyTarget.group} onValueChange={v => setCopyTarget(p => ({ ...p, group: v }))}>
                    <SelectTrigger className="border-[#DED8EB] h-8 text-xs" data-testid="copy-tgt-group"><SelectValue placeholder="Same group" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="same">Same Group</SelectItem>
                      {GROUPS.map(g => <SelectItem key={g} value={String(g)}>Group {g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            {copyResult && (
              <div className={`p-3 rounded-lg text-sm ${copyResult.error ? 'bg-red-50 text-[#D9534F] border border-red-200' : 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]'}`} data-testid="copy-result">
                {copyResult.error ? copyResult.error : `Copied ${copyResult.created} activities (${copyResult.skipped} already existed)`}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)} className="border-[#DED8EB]">Close</Button>
            <Button onClick={handleCopy} disabled={copying || !copyTarget.date} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1" data-testid="copy-activities-submit">
              {copying ? 'Copying...' : 'Copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Block Presets Dialog */}
      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="presets-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Settings2 className="h-5 w-5 text-[#6B3FA0]" /> Time Block Presets
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#5A4D6E]">Configure reusable time blocks for quick activity scheduling.</p>
            {editPresets.map((p, i) => (
              <div key={`edit-preset-${i}`} className="flex items-center gap-2 p-2 rounded-lg border border-[#DED8EB] bg-[#F7F4EF]">
                <Input value={p.name} onChange={e => { const n = [...editPresets]; n[i].name = e.target.value; setEditPresets(n); }}
                  placeholder="Name" className="border-[#DED8EB] h-7 text-xs flex-1" />
                <Input type="time" value={p.time_start} onChange={e => { const n = [...editPresets]; n[i].time_start = e.target.value; setEditPresets(n); }}
                  className="border-[#DED8EB] h-7 text-xs w-24" style={{ fontFamily: 'IBM Plex Mono, monospace' }} />
                <span className="text-xs text-[#8E85A0]">—</span>
                <Input type="time" value={p.time_end} onChange={e => { const n = [...editPresets]; n[i].time_end = e.target.value; setEditPresets(n); }}
                  className="border-[#DED8EB] h-7 text-xs w-24" style={{ fontFamily: 'IBM Plex Mono, monospace' }} />
                <button onClick={() => setEditPresets(editPresets.filter((_, j) => j !== i))} className="text-[#8E85A0] hover:text-[#D9534F]"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setEditPresets([...editPresets, { name: '', time_start: '09:00', time_end: '10:30' }])}
              className="border-[#DED8EB] text-[#6B3FA0] gap-1" data-testid="add-preset-btn">
              <Plus className="h-3 w-3" /> Add Block
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetOpen(false)} className="border-[#DED8EB]">Cancel</Button>
            <Button onClick={handleSavePresets} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="save-presets-btn">Save Presets</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tap-to-pick group dialog (touch-friendly fallback for drag) */}
      <Dialog open={!!pickerForBank} onOpenChange={(o) => !o && setPickerForBank(null)}>
        <DialogContent className="sm:max-w-sm bg-white" data-testid="bank-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#2D1B4E]">Add &ldquo;{pickerForBank?.item?.name}&rdquo; to…</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#8E85A0] mb-2">Pick a group for {format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMM d')}.</p>
          <div className="grid grid-cols-2 gap-2">
            {GROUPS.map(g => {
              const staffName = getGroupStaff(g, selectedDate);
              return (
                <button
                  key={g}
                  onClick={() => {
                    const item = pickerForBank.item;
                    setPickerForBank(null);
                    setEditing(null);
                    setExtraGroups([]);
                    setForm(formFromBankItem(item, g, selectedDate));
                    setDialogOpen(true);
                  }}
                  className="text-left p-2.5 rounded-md border border-[#DED8EB] bg-white hover:border-[#6B3FA0] hover:bg-[#F8F5FB] transition-all"
                  data-testid={`picker-group-${g}`}>
                  <Badge className="text-[10px] text-white" style={{ backgroundColor: GROUP_COLORS[g - 1] }}>Group {g}</Badge>
                  {staffName && <p className="text-xs text-[#2D1B4E] mt-1 font-medium truncate">{staffName}</p>}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity Bank Manager — full database view */}
      <BankManagerDialog
        open={bankManagerOpen}
        onClose={() => setBankManagerOpen(false)}
        bank={activityBank}
        isScheduler={isScheduler}
        api={api}
        onChanged={fetchData}
        onAssign={(item) => { setBankManagerOpen(false); setPickerForBank({ item }); }}
        onDragStart={(item) => setDraggingName(item.name)}
        onDragEnd={() => { setDraggingName(''); setDropTargetGroup(0); }}
      />
    </div>
  );
}
